import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { GridType } from 'rev-shared/analytics/Constant';
import { ILinkCellRendererParams } from 'rev-shared/ui/dataGrid/cellRenderers/LinkCellRenderer.Component';
import { IVbUiDataGridValueFormatterParams } from 'rev-shared/ui/dataGrid/valueFormatters/IVbUiDataGridValueFormatterParams';
import { IVbUiDataGridColDef } from 'rev-shared/ui/dataGrid/columns/IVbUiDataGridColDef';
import { mediumDateTimeValueFormatter } from 'rev-shared/ui/dataGrid/valueFormatters/MediumDateTimeValueFormatter';

import { getEventUrl } from 'rev-portal/scheduledEvents/webcast/Constants';

import { EventAnalyticsService } from './EventAnalytics.Service';
import { MAX_LIVE_EVENTS_COUNT } from '../Constant';

import styles from './EventAnalytics.Component.module.less';

const TRANSLATION_KEY = {
	Broadcasting: 'Broadcasting',
	InProgress: 'InProgress',
	Recording: 'Broadcasting',
	RecordingStarting: 'Broadcasting',
	RecordingStopping: 'Broadcasting',
	Starting: 'InProgress',
	VideoSourceStarting: 'InProgress',
	WaitingForStream: 'InProgress'
};

@Component({
	selector: 'live-events-grid',
	templateUrl: './LiveEventsGrid.Component.html',
	host: {
		'[class]': 'hostStyles'
	}
})
export class LiveEventsGridComponent implements OnInit {
	@Input() public accountId: string;
	@Input() public header: string;
	@Input() public pollingIntervalSecs: number;

	public readonly mediumDateTimeValueFormatter = mediumDateTimeValueFormatter;
	public readonly GridType = GridType;
	public readonly pageSize = 15;
	public readonly styles = styles;

	public readonly hostStyles = `${styles.liveEventsGridRoot} width-full padding-full-5`;
	public readonly statusValueFormatter = statusValueFormatter;
	public readonly titleCellParams: ILinkCellRendererParams<any> = {
		getCellCfg: event => ({
			href: event.canEdit ? '#' + getEventUrl(event.id) : undefined
		})
	};
	public moreRecordsText: string;

	public showMoreRecordsRowRenderer = () => {
		const eDiv = document.createElement('div');
		eDiv.innerHTML =
			`<div class="${styles.moreRecords}">${this.moreRecordsText}</div>`;
		return eDiv.firstChild;
	};
	public columnDefs: IVbUiDataGridColDef[];

	constructor(
		public EventAnalyticsService: EventAnalyticsService,
		private TranslateService: TranslateService
	) {
		this.moreRecordsText = TranslateService.instant('Event_Analytics_LiveEvents_MoreRecordsMessage', { '0': MAX_LIVE_EVENTS_COUNT });
	}

	public ngOnInit(): void {
		this.assignColumnDefs();
	}

	private assignColumnDefs(): void {
		this.columnDefs = [
			{
				field: 'startDate',
				headerName: this.TranslateService.instant('Reports_Column_ScheduledStartDate'),
				valueFormatter: this.mediumDateTimeValueFormatter,
				filter: false,
				sortable: false
			},
			{
				field: 'title',
				headerName: this.TranslateService.instant('Reports_Column_EventTitle'),
				tooltipField: 'title',
				filter: true,
				sortable: false,
				cellRenderer: 'link',
				cellRendererParams: this.titleCellParams
			},
			{
				field: 'status',
				headerName: this.TranslateService.instant('Status'),
				valueFormatter: this.statusValueFormatter,
				sortable: false,
				filter: false
			},
			{
				field: 'attendeesCount',
				headerName: this.TranslateService.instant('Reports_Column_Attendees'),
				sortable: false,
				filter: false
			},
			{
				field: 'rebufferingPerc',
				headerName: this.TranslateService.instant('Reports_Column_ExperiencedRebuffering'),
				sortable: false,
				filter: false
			},
			{
				field: 'avgRebufferingDurationInSeconds',
				headerName: this.TranslateService.instant('Reports_Column_AverageExperiencedRebufferDurationInSecs'),
				sortable: false,
				filter: false
			},
			{
				field: 'experiencedErrors',
				headerName: this.TranslateService.instant('Reports_Column_ExperiencedErrorsByAttendees'),
				sortable: false,
				filter: false
			}
		];
	}
}

function statusValueFormatter(params: IVbUiDataGridValueFormatterParams): string {
	const translationKey: string = TRANSLATION_KEY[params.value];

	return translationKey ? params.context.getTranslation(translationKey) : params.value;
}
